import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { Modal } from "../../../../../../_metronic/elements/Modal";
import { useViewFieldContext } from "../ViewFieldContext";
import { useViewLandContext } from "../ViewLand/ViewLandContext";
import { useErrorHandler } from "../../../../../../_metronic/helpers/ErrorHandler";
import { openAlert } from "../../../../../../_metronic/elements/NotificationAlert";
import { useMapLayersV2 } from "../../../Map/MapLayersV2Context";
import * as GisDataAdapter from "../../services/GisDataAdapter";
import { useSearchContext } from "../../../Toolbar/SearchContext";
import { useTranslation } from "react-i18next";
import { PlanetHdAccessCheck, PlanetHdImpactNotice } from "../PlanetHdLandChangeWarning";
import { HD_ACCESS_ACTIVE, HD_ACCESS_CHECKING, HD_ACCESS_ERROR, PLANET_HD_VISUALIZATION_ACCESS_PARAMS, resolveHdAccessCheck } from "../planetHdLandChange";

const ModalHeader = () => {
    const { land } = useViewLandContext();
    const { t } = useTranslation();

    return (
        <div className='modal-header p-5'>
            <h5 className='modal-title'>{t("lands:delete.title", { name: land?.name || '' })}</h5>
        </div>
    );
};

const ModalFooter = ({ deleteLand, disabled, loading, setOpen }) => {
    const { t } = useTranslation();
    return (
        <div className='modal-footer'>
            <button disabled={loading === true} type='button' className='btn btn-light' onClick={() => setOpen(false)}>
                {t("lands:delete.buttons.cancel")}
            </button>
            <button disabled={disabled} onClick={deleteLand} type='button' className='btn btn-danger'>
                {!loading && t("lands:delete.buttons.delete")}
                {loading && (
                    <span className='indicator-progress' style={{ display: "block" }}>
                        {t("lands:delete.messages.deleting")} <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                )}
            </button>
        </div>
    );
};

const DeleteLand = ({ open, setOpen }) => {
    const [loading, setLoading] = useState(false);
    const { field_id, land_id } = useParams();
    const { load } = useViewFieldContext();
    const { errorHandler } = useErrorHandler();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const search = useSearchContext();
    const mapLayers = useMapLayersV2();
    const viewLand = useViewLandContext();
    const deleteInFlightRef = useRef(false);
    const errorHandlerRef = useRef(errorHandler);
    const [hdAccessStatus, setHdAccessStatus] = useState(HD_ACCESS_CHECKING);
    const [hdAccessCheckVersion, setHdAccessCheckVersion] = useState(0);

    errorHandlerRef.current = errorHandler;

    useEffect(() => {
        if (!open || !land_id) {
            return undefined;
        }

        let active = true;
        setHdAccessStatus(HD_ACCESS_CHECKING);

        errorHandlerRef.current(GisDataAdapter.getLandHdAccess(land_id, PLANET_HD_VISUALIZATION_ACCESS_PARAMS), [404, 500]).then((res) => {
            if (active) {
                setHdAccessStatus(resolveHdAccessCheck(res));
            }
        });

        return () => {
            active = false;
        };
    }, [hdAccessCheckVersion, land_id, open]);

    const retryHdAccessCheck = () => {
        setHdAccessCheckVersion((current) => current + 1);
    };

    const deleteLand = async () => {
        if (deleteInFlightRef.current) return;
        deleteInFlightRef.current = true;
        setLoading(true);
        viewLand.suspendLandRequests(land_id);
        let deleted = false;
        try {
            const res = await errorHandler(GisDataAdapter.deleteLand(land_id));
            if (!res?.ok) return;

            deleted = true;
            viewLand.forgetDeletedLand(land_id);
            setOpen(false);
            navigate(`/gis/field/view/${field_id}/`);
            openAlert("success", t("lands:delete.messages.success"), "");
            mapLayers.reloadSource();
            load();
            search.load();
        } finally {
            if (!deleted) viewLand.resumeLandRequests(land_id);
            deleteInFlightRef.current = false;
            setLoading(false);
        }
    };

    return (
        <>
            <Modal open={open} setOpen={setOpen}>
                <div className='modal-dialog modal-dialog-centered'>
                    <div className='modal-content'>
                        <ModalHeader />
                        <div className='modal-body p-5'>
                            <p>{t("lands:delete.confirmation")}</p>
                            <PlanetHdAccessCheck status={hdAccessStatus} onRetry={retryHdAccessCheck} />
                            {hdAccessStatus === HD_ACCESS_ACTIVE && <PlanetHdImpactNotice action='delete' />}
                        </div>
                        <ModalFooter deleteLand={deleteLand} setOpen={setOpen} loading={loading} disabled={loading || hdAccessStatus === HD_ACCESS_CHECKING || hdAccessStatus === HD_ACCESS_ERROR} />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DeleteLand;
